import { QuoteProductDTO } from 'pages/quotes/types';
import { useCallback, useEffect, useRef } from 'react';

const DEBOUNCE_DELAY = 400;

interface UseQuotePricingCalculationsProps {
  updatePricing: (
    itemID: string,
    updates: Partial<{
      quantity: number;
      sell: number | string;
      totalGrossProfit: number | string;
      totalGrossProfitPercentage: number | string;
    }>
  ) => void;
}

export const useQuotePricingCalculations = ({
  updatePricing
}: UseQuotePricingCalculationsProps) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdateRef = useRef<{
    itemID: string;
    updates: Partial<{
      quantity: number;
      sell: number | string;
      totalGrossProfit: number | string;
      totalGrossProfitPercentage: number | string;
    }>;
  } | null>(null);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Flush pending update on unmount
        if (pendingUpdateRef.current) {
          updatePricing(
            pendingUpdateRef.current.itemID,
            pendingUpdateRef.current.updates
          );
        }
      }
    },
    [updatePricing]
  );

  const debouncedUpdatePricing = useCallback(
    (
      itemID: string,
      updates: Partial<{
        quantity: number;
        sell: number | string;
        totalGrossProfit: number | string;
        totalGrossProfitPercentage: number | string;
      }>
    ) => {
      // Store pending update
      pendingUpdateRef.current = { itemID, updates };

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (pendingUpdateRef.current) {
          updatePricing(
            pendingUpdateRef.current.itemID,
            pendingUpdateRef.current.updates
          );
          pendingUpdateRef.current = null;
        }
        debounceTimerRef.current = null;
      }, DEBOUNCE_DELAY);
    },
    [updatePricing]
  );

  const handleQuantityChange = useCallback(
    (itemID: string, quantity: number) => {
      debouncedUpdatePricing(itemID, {
        quantity
      });
    },
    [debouncedUpdatePricing]
  );

  const handleSellChange = useCallback(
    (itemID: string, product: QuoteProductDTO, sell: string) => {
      if (sell !== '') {
        const cost = Number(product.cost);
        const sellV = Number(sell);

        const totalGrossProfit = sellV - cost;

        const totalGrossProfitPercentage =
          sellV === 0
            ? 0
            : Math.min(
                parseFloat((((sellV - cost) / sellV) * 100).toFixed(2)),
                99.99
              );

        debouncedUpdatePricing(itemID, {
          sell: sellV,
          totalGrossProfit: totalGrossProfit.toFixed(2),
          totalGrossProfitPercentage
        });
      } else {
        debouncedUpdatePricing(itemID, {
          sell: '',
          totalGrossProfit: product.totalGrossProfit,
          totalGrossProfitPercentage: product.totalGrossProfitPercentage
        });
      }
    },
    [debouncedUpdatePricing]
  );

  const handleProfitMarginChange = useCallback(
    (itemID: string, product: QuoteProductDTO, totalGrossProfit: string) => {
      if (totalGrossProfit !== '') {
        const cost = Number(product.cost);
        const profit = Number(totalGrossProfit);

        const sellPerUnit = parseFloat((cost + profit).toFixed(2));

        const totalGrossProfitPercentage =
          sellPerUnit === 0
            ? 0
            : Math.min(
                parseFloat(
                  (((sellPerUnit - cost) / sellPerUnit) * 100).toFixed(2)
                ),
                99.99
              );

        debouncedUpdatePricing(itemID, {
          sell: sellPerUnit,
          totalGrossProfit,
          totalGrossProfitPercentage
        });
      } else {
        debouncedUpdatePricing(itemID, {
          sell: product.sell,
          totalGrossProfit: '',
          totalGrossProfitPercentage: product.totalGrossProfitPercentage
        });
      }
    },
    [debouncedUpdatePricing]
  );

  const handleProfitMarginPercentageChange = useCallback(
    (
      itemID: string,
      product: QuoteProductDTO,
      profitMarginPercentage: string
    ) => {
      if (profitMarginPercentage === '') {
        debouncedUpdatePricing(itemID, {
          sell: product.sell,
          totalGrossProfit: product.totalGrossProfit,
          totalGrossProfitPercentage: ''
        });
        return;
      }

      const cost = Number(product?.cost);
      const margin = Math.min(Number(profitMarginPercentage), 99.99);
      const sellPerUnit = cost / (1 - margin / 100);
      const totalGrossProfit = sellPerUnit - cost;

      debouncedUpdatePricing(itemID, {
        sell: sellPerUnit,
        totalGrossProfit,
        totalGrossProfitPercentage: margin
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    handleQuantityChange,
    handleSellChange,
    handleProfitMarginChange,
    handleProfitMarginPercentageChange
  };
};
